import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useGetWPPlanDetailQuery } from '@/redux/service/weeklyPlanning'
import {
  CalendarDays,
  User,
  Users,
  Clock,
  FileText,
  CheckCircle2,
  Loader2,
  ClipboardList,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'

interface WeeklyViewModelProps {
  isOpen: boolean
  onClose: () => void
  planId: number | null
}

const WeeklyViewModel: React.FC<WeeklyViewModelProps> = ({
  isOpen,
  onClose,
  planId,
}) => {
  const [currentPlanId, setCurrentPlanId] = useState<number | null>(planId)

  useEffect(() => {
    setCurrentPlanId(planId)
  }, [planId])

  const {
    data: response,
    isLoading,
    isError,
  } = useGetWPPlanDetailQuery(currentPlanId!, {
    skip: !currentPlanId || !isOpen,
  })

  const plan = response?.data

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogContent className='sm:max-w-5xl p-0 overflow-hidden bg-background rounded-2xl border-border shadow-lg'>
        {isLoading ? (
          <div className='h-[400px] flex flex-col items-center justify-center gap-3'>
            <Loader2 className='h-8 w-8 animate-spin text-primary' />
            <p className='text-sm font-medium text-muted-foreground'>
              Fetching plan details...
            </p>
          </div>
        ) : isError || !plan ? (
          <div className='h-[300px] flex flex-col items-center justify-center text-center p-6'>
            <div className='p-4 bg-destructive/10 rounded-full mb-4'>
              <FileText className='h-8 w-8 text-destructive' />
            </div>
            <h3 className='text-lg font-bold text-foreground'>
              Failed to load plan
            </h3>
            <p className='text-sm text-muted-foreground mt-1 max-w-xs'>
              We couldn't retrieve the details for this planning session. Please
              try again later.
            </p>
            <Button variant='outline' onClick={onClose} className='mt-6'>
              Close
            </Button>
          </div>
        ) : (
          <div className='flex flex-col max-h-[90vh]'>
            {/* Header */}
            <DialogHeader className='px-6 py-4 border-b border-border bg-muted/30 shrink-0'>
              <DialogTitle className='flex items-center gap-3'>
                <div className='p-2 bg-primary text-primary-foreground rounded-lg shrink-0 shadow-sm'>
                  <ClipboardList className='w-4 h-4' />
                </div>
                <div className='flex-1 min-w-0'>
                  <p className='text-base font-bold text-foreground'>
                    Weekly Resource Plan
                  </p>
                  <div className='flex items-center gap-2 mt-0.5'>
                    <span className='inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-green-500/10 text-green-600 border border-green-500/20'>
                      <CheckCircle2 className='h-3 w-3' />
                      {plan.status}
                    </span>
                    <span className='text-sm font-normal text-muted-foreground truncate'>
                      {plan.member_name}{' '}
                      <span className='text-muted-foreground/50'>·</span>{' '}
                      {plan.team}{' '}
                      <span className='text-muted-foreground/50'>·</span> Week
                      of {format(parseISO(plan.week_start), 'd MMM yyyy')}
                    </span>
                  </div>
                </div>
              </DialogTitle>
            </DialogHeader>

            {/* Scrollable Content */}
            <div className='flex-1 overflow-y-auto px-6 py-6 space-y-8'>
              {/* Info Cards */}
              <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
                <div className='flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border'>
                  <User className='h-4 w-4 text-primary' />
                  <div>
                    <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight'>
                      Member
                    </p>
                    <p className='text-xs font-bold'>{plan.member_name}</p>
                  </div>
                </div>
                <div className='flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border'>
                  <Users className='h-4 w-4 text-primary' />
                  <div>
                    <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight'>
                      Team
                    </p>
                    <p className='text-xs font-bold'>{plan.team}</p>
                  </div>
                </div>
                <div className='flex items-center gap-3 p-3 bg-muted/30 rounded-xl border border-border'>
                  <CalendarDays className='h-4 w-4 text-primary shrink-0' />

                  <div className='flex-1'>
                    <p className='text-[10px] font-bold text-muted-foreground uppercase tracking-tight'>
                      Period
                    </p>

                    <div className='flex items-center gap-2'>
                      <Button
                        variant='outline'
                        size='icon'
                        className='h-6 w-6'
                        disabled={!plan.previous_plan_id}
                        onClick={() => setCurrentPlanId(plan.previous_plan_id)}
                      >
                        <ChevronLeft className='h-3 w-3' />
                      </Button>

                      <p className='text-xs font-bold'>
                        {format(parseISO(plan.week_start), 'MMM d')} -{' '}
                        {format(parseISO(plan.week_end), 'MMM d')}
                      </p>
                      <Button
                        variant='outline'
                        size='icon'
                        className='h-6 w-6'
                        disabled={!plan.next_plan_id}
                        onClick={() => setCurrentPlanId(plan.next_plan_id)}
                      >
                        <ChevronRight className='h-3 w-3' />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* This Week Section */}
              <section>
                <div className='flex items-center gap-2 mb-4'>
                  <h3 className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
                    Plan for This Week
                  </h3>
                  <div className='h-px flex-1 bg-border' />
                </div>

                <div className='space-y-6'>
                  {plan.plan_projects.map(project => (
                    <div key={project.project_id}>
                      <div className='flex items-center justify-between mb-2'>
                        <h4 className='text-sm font-bold text-foreground'>
                          {project.project_name}
                        </h4>
                        <span className='text-[11px] font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-md'>
                          {project.total_hours}h total
                        </span>
                      </div>

                      <div className='space-y-1.5'>
                        {project.tasks.map(task => (
                          <div
                            key={task.id}
                            className='flex items-center gap-3 px-3 py-2.5 bg-card rounded-lg border border-border'
                          >
                            <div className='flex-1 min-w-0'>
                              <p className='text-sm font-medium text-foreground '>
                                {task.task_name}
                              </p>
                            </div>
                            <span
                              className={cn(
                                'text-[11px] font-semibold px-2 py-1 rounded-md border shrink-0',
                                task.is_billable
                                  ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                  : 'bg-muted text-muted-foreground border-border'
                              )}
                            >
                              {task.is_billable ? 'Billable' : 'Non-Billable'}
                            </span>
                            <div className='flex items-center gap-1.5 shrink-0 min-w-[45px] justify-end'>
                              <Clock className='h-3 w-3 text-muted-foreground' />
                              <span className='text-sm font-bold text-foreground'>
                                {task.estimated_hours}h
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}

                  {plan.plan_projects.length === 0 && (
                    <div className='py-8 flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl'>
                      <p className='text-xs text-muted-foreground'>
                        No tasks planned for this week
                      </p>
                    </div>
                  )}
                </div>
              </section>

              {/* Backlog Section */}
              {plan.backlog_projects.length > 0 && (
                <section>
                  <div className='flex items-center gap-2 mb-4'>
                    <h3 className='text-[11px] font-bold uppercase tracking-widest text-muted-foreground'>
                      Backlog & Carryover
                    </h3>
                    <div className='h-px flex-1 bg-border' />
                  </div>

                  <div className='space-y-6'>
                    {plan.backlog_projects.map(project => (
                      <div key={project.project_id}>
                        <div className='flex items-center justify-between mb-2'>
                          <h4 className='text-sm font-bold text-foreground'>
                            {project.project_name}
                          </h4>
                          <span className='text-[11px] font-bold text-muted-foreground bg-muted px-2 py-0.5 rounded-md'>
                            {project.total_hours}h total
                          </span>
                        </div>
                        <div className='space-y-1.5'>
                          {project.tasks.map(task => (
                            <div
                              key={task.id}
                              className='flex items-center gap-3 px-3 py-2.5 bg-card rounded-lg border border-border'
                            >
                              <div className='flex-1 min-w-0'>
                                <p className='text-sm font-medium text-foreground truncate'>
                                  {task.task_name}
                                </p>
                              </div>
                              <span
                                className={cn(
                                  'text-[11px] font-semibold px-2 py-1 rounded-md border shrink-0',
                                  task.is_billable
                                    ? 'bg-green-500/10 text-green-600 border-green-500/20'
                                    : 'bg-muted text-muted-foreground border-border'
                                )}
                              >
                                {task.is_billable ? 'Billable' : 'Non-Billable'}
                              </span>
                              <div className='flex items-center gap-1.5 shrink-0 min-w-[45px] justify-end'>
                                <Clock className='h-3 w-3 text-muted-foreground' />
                                <span className='text-sm font-bold text-foreground'>
                                  {task.estimated_hours}h
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Summary Totals */}
              <div className='grid grid-cols-3 gap-3 pt-4 border-t border-border'>
                <div className='bg-green-500/10 rounded-xl p-3 text-center border border-green-500/10'>
                  <p className='text-[10px] text-green-600 font-bold uppercase tracking-wide'>
                    Billable
                  </p>
                  <p className='text-xl font-bold text-green-600'>
                    {(
                      plan.plan_projects.reduce(
                        (acc, p) =>
                          acc +
                          p.tasks
                            .filter(t => t.is_billable)
                            .reduce((s, t) => s + Number(t.estimated_hours), 0),
                        0
                      ) +
                      plan.backlog_projects.reduce(
                        (acc, p) =>
                          acc +
                          p.tasks
                            .filter(t => t.is_billable)
                            .reduce((s, t) => s + Number(t.estimated_hours), 0),
                        0
                      )
                    ).toFixed(1)}
                    h
                  </p>
                </div>
                <div className='bg-muted rounded-xl p-3 text-center border border-border'>
                  <p className='text-[10px] text-muted-foreground font-bold uppercase tracking-wide'>
                    Non-Billable
                  </p>
                  <p className='text-xl font-bold text-foreground'>
                    {(
                      plan.plan_projects.reduce(
                        (acc, p) =>
                          acc +
                          p.tasks
                            .filter(t => !t.is_billable)
                            .reduce((s, t) => s + Number(t.estimated_hours), 0),
                        0
                      ) +
                      plan.backlog_projects.reduce(
                        (acc, p) =>
                          acc +
                          p.tasks
                            .filter(t => !t.is_billable)
                            .reduce((s, t) => s + Number(t.estimated_hours), 0),
                        0
                      )
                    ).toFixed(1)}
                    h
                  </p>
                </div>
                <div className='bg-primary/10 rounded-xl p-3 text-center border border-primary/10'>
                  <p className='text-[10px] text-primary font-bold uppercase tracking-wide'>
                    Total Effort
                  </p>
                  <p className='text-xl font-bold text-primary'>
                    {(
                      plan.plan_projects.reduce(
                        (acc, p) => acc + p.total_hours,
                        0
                      ) +
                      plan.backlog_projects.reduce(
                        (acc, p) => acc + p.total_hours,
                        0
                      )
                    ).toFixed(1)}
                    h
                  </p>
                </div>
              </div>

              {/* Bench Date & Notes */}
              <div className='flex gap-6 pt-4 border-t border-border'>
                <div className='space-y-1.5 w-48 shrink-0'>
                  <p className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest'>
                    Est. Bench Date
                  </p>
                  <div className='flex items-center gap-2 p-2.5 bg-muted/30 rounded-lg border border-border'>
                    <CalendarDays className='h-4 w-4 text-primary' />
                    <span className='text-sm font-bold'>
                      {plan.est_bench_date
                        ? format(parseISO(plan.est_bench_date), 'dd MMM yyyy')
                        : 'N/A'}
                    </span>
                  </div>
                </div>

                {plan.notes && (
                  <div className='space-y-1.5 flex-1'>
                    <p className='text-[11px] font-bold text-muted-foreground uppercase tracking-widest'>
                      Additional Notes
                    </p>
                    <div className='p-3 bg-muted/30 rounded-lg border border-border text-sm text-muted-foreground leading-relaxed italic'>
                      "{plan.notes}"
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className='px-6 py-4 bg-muted/30 border-t border-border flex justify-end gap-3'>
              <Button onClick={onClose} className='px-8'>
                Done
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default WeeklyViewModel
